import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import AfterContractCertificateSessionsPage from './AfterContractCertificateSessions'
import WorkSupportDiseaseCodeTool from './WorkSupportDiseaseCodeTool'
import PolicyDeliveryBookDownloadTool from './PolicyDeliveryBookDownloadTool'
import MenuGuideButton from '../components/MenuGuideButton'

const WORK_TOOLS = [
  {
    id: 'policy-delivery-books',
    label: '증권전달북',
  },
  {
    id: 'after-contract-certificates',
    label: '3종확인서(인카)',
  },
  {
    id: 'disease-code-search',
    label: '질병코드 검색',
  },
]

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

export default function WorkSupportToolsPage() {
  const { hasAnyRole } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [afterContractAllowed, setAfterContractAllowed] = useState(null)
  const isOperator = Boolean(hasAnyRole?.([Roles.OPERATOR]))

  useEffect(() => {
    if (isOperator) {
      setAfterContractAllowed(true)
      return undefined
    }
    setAfterContractAllowed(null)
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/after-contract/certificates/eligibility')
        const payload = unwrap(res)
        if (!cancelled) setAfterContractAllowed(Boolean(payload?.allowed))
      } catch {
        if (!cancelled) setAfterContractAllowed(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOperator])

  const tools = useMemo(() => {
    if (isOperator || afterContractAllowed === true) return WORK_TOOLS
    return WORK_TOOLS.filter((t) => t.id !== 'after-contract-certificates')
  }, [afterContractAllowed, isOperator])

  const defaultToolId = tools[0]?.id || 'disease-code-search'

  const activeTool = useMemo(() => {
    const id = searchParams.get('tool')
    if (id && tools.some((t) => t.id === id)) return id
    return defaultToolId
  }, [searchParams, tools, defaultToolId])

  useEffect(() => {
    if (isOperator) return
    if (afterContractAllowed !== false) return
    const id = searchParams.get('tool')
    if (id !== 'after-contract-certificates') return
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('tool')
    setSearchParams(nextParams, { replace: true })
  }, [afterContractAllowed, isOperator, searchParams, setSearchParams])

  const selectTool = (toolId) => {
    const nextParams = new URLSearchParams(searchParams)
    if (toolId === defaultToolId) {
      nextParams.delete('tool')
    } else {
      nextParams.set('tool', toolId)
    }
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">업무지원도구</h1>
            <MenuGuideButton slug="work-tools" title="업무지원도구 가이드" />
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(150px,10%)_minmax(0,90%)]">
        <aside className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm lg:sticky lg:top-24 lg:self-start">
          <div className="mb-2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">도구 목록</div>
          <div className="flex gap-1 overflow-x-auto lg:block lg:space-y-1 lg:overflow-visible">
            {tools.map((tool) => (
              <button
                key={tool.id}
                type="button"
                className={`min-w-[128px] rounded-lg px-2.5 py-2 text-left transition lg:w-full ${
                  activeTool === tool.id ? 'border border-sgez-primary bg-sky-50 text-sgez-primary shadow-sm ring-1 ring-sgez-primary/10' : 'text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => selectTool(tool.id)}
              >
                <div className="text-xs font-bold">{tool.label}</div>
              </button>
            ))}
          </div>
        </aside>

        <main className="min-w-0">
          {activeTool === 'policy-delivery-books' && <PolicyDeliveryBookDownloadTool />}
          {activeTool === 'after-contract-certificates' && <AfterContractCertificateSessionsPage embedded />}
          {activeTool === 'disease-code-search' && <WorkSupportDiseaseCodeTool />}
        </main>
      </div>
    </div>
  )
}
